import { motion } from 'framer-motion';

interface CurationCardProps {
  name: string;
  subtitle: string;
  area: string;
  recommendation: string;
  tags: string[];
  reviewSnippet: string;
  reviews: number;
  image: string;
  reviewImages: string[];
  index: number;
}

export default function CurationCard({
  name,
  subtitle,
  area,
  recommendation,
  tags,
  reviewSnippet,
  reviews,
  image,
  reviewImages,
  index,
}: CurationCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
      className="bg-white rounded-2xl overflow-hidden shadow-[0_2px_16px_rgba(26,39,68,0.06)]"
    >
      {/* Representative image: 4:3 editorial feel */}
      <div className="relative w-full aspect-[4/3] overflow-hidden">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/20 via-transparent to-transparent" />
      </div>

      <div className="p-5">
        {/* Shop name + one-line description */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-base font-semibold text-navy tracking-tight">{name}</h4>
            <span className="text-[11px] text-navy-light">{area}</span>
          </div>
          <p className="text-xs text-navy-light leading-relaxed">{subtitle}</p>
        </div>

        {/* Why recommended — the core logic */}
        <div className="bg-cream rounded-xl p-4 mb-4">
          <div className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-softgold/10 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-lightbulb-line text-softgold text-[10px]" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-navy mb-1">왜 이 업체를 추천드릴까요</p>
              <p className="text-[11px] text-navy-light leading-relaxed">{recommendation}</p>
            </div>
          </div>
        </div>

        {/* Strength tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-2.5 py-1 rounded-full bg-navy/5 text-navy-light font-medium"
            >
              #{tag}
            </span>
          ))}
        </div>

        {/* Review-based snippet */}
        <div className="flex items-start gap-2.5 mb-4">
          <div className="w-5 h-5 rounded-full bg-navy/5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-chat-quote-line text-navy text-[10px]" />
          </div>
          <p className="text-[11px] text-navy-light leading-relaxed italic">
            “{reviewSnippet}”
          </p>
        </div>

        {/* Bottom: photo strip + review count */}
        <div className="border-t border-cream-dark pt-3">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              {reviewImages.slice(0, 4).map((src, i) => (
                <div
                  key={i}
                  className="w-14 h-14 rounded-lg overflow-hidden shrink-0"
                >
                  <img
                    src={src}
                    alt={`${name} 후기 ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <div className="ml-auto shrink-0">
              <p className="text-[10px] text-navy-light">
                실제 후기 <span className="font-semibold text-navy">{reviews}</span>개
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}